import { useNavigation } from '@portals/core/src/components/Navigation/Provider';
import { useSPKCookie } from '@portals/forms/src/hooks/useSPKCookie/useSPKCookie';
import { TrackingContext } from '@portals/tracking/src/context/tracking';
import { NextRouter } from 'next/router';
import { MouseEventHandler, useContext } from 'react';

import {
  isLinkActionLink,
  isLinkAssetLink,
  isLinkExternalLink,
  isLinkInternalLink,
  isLinkMenuLink,
  isLinkThematischerLink,
  LinkActionLink,
  LinkAssetLink,
  LinkExternalLink,
  LinkInternalLink,
  LinkThematischerLink,
} from '../../models';
import { useRequestHeaders } from '../contexts/request-headers';
import { useServiceBoxContext } from '../contexts/service-box';
import { useSparkassenAssignmentForm } from '../contexts/sparkasse-assignment-form';
import { useThematicLinkContext } from '../contexts/thematic-link';
import { whiteList } from '../contexts/user-context';
import { isTargetSelfDomain } from '../utils/isTargetSelfDomain';
import { isWhitelistedDomain } from '../utils/isWhitelistedDomain';
import { useOptionalRouter } from './useOptionalRouter';

declare global {
  interface TagCommander {
    privacyCenter: {
      showPrivacyCenter: () => void;
    };
  }

  const tC: TagCommander;
}

export const EVENT_THEMATIC_LINK = 'spk.link.thematic.clicked';

export interface Link {
  __component: `link.${string}`;
  id: string;
  text: string;
  title?: string;
  url?: LinkExternalLink['url'];
  key?: LinkThematischerLink['key'];
  description?: LinkThematischerLink['description'];
  action?: LinkActionLink['action'];
  media?: Pick<LinkAssetLink['media'], 'id' | 'name' | 'hash'>;
  reference?: Pick<LinkInternalLink['reference'], 'id' | 'slugPath' | 'publication_id'>;
  anchor?: string;
  target?: '_blank' | '_self';
  rel?: {
    // cms attributes
    nofollow?: boolean;
    sponsored?: boolean;
    ugc?: boolean;

    // additional attributes
    noopener?: boolean;
    noreferrer?: boolean;
  };
}

interface Return {
  linkProps: {
    component: 'div' | 'button' | 'a';
    tabIndex: 0 | -1;
    'aria-current'?: 'page' | 'location';
    role?: 'link' | 'button';
    'aria-label'?: string;
    onClick?: MouseEventHandler<HTMLElement>;
    disabled?: boolean;
  };
  anchorProps: {
    component: 'div' | 'button' | 'a';
    tabIndex: 0 | -1;
    href?: string;
    title?: string;
    onClick?: MouseEventHandler<HTMLElement>;
    download?: boolean;
    target?: '_blank' | '_self';
    rel?: string;
    disabled?: boolean;
  };
  singleProps: {
    component: 'div' | 'button' | 'a' | 'span';
    tabIndex: 0 | -1;
    'aria-current'?: 'page' | 'location';
    href?: string;
    title?: string;
    role?: 'link' | 'button';
    onClick?: MouseEventHandler<HTMLElement>;
    download?: boolean;
    target?: '_blank' | '_self';
    rel?: string;
    disabled?: boolean;
  };
}

const checkLocation = (router: NextRouter | undefined, pathname) => {
  const currentPathSlug = router?.asPath?.replace(/.html$/, '');
  const pagePathSlug = pathname.replace(/.html$/, '');

  if (pagePathSlug === router?.pathname || pagePathSlug === currentPathSlug) {
    return 'page';
  }

  if (router?.pathname?.includes(pagePathSlug) || router?.asPath?.includes(pagePathSlug)) {
    return 'location';
  }

  return null;
};

const makeRel = (rel: Link['rel']) => {
  return Object.entries(rel)
    .filter(([, value]) => value === true)
    .map(([key]) => key)
    .join(' ');
};

export function useLinkProps(link: Link): Return {
  const router = useOptionalRouter();
  const { internalHost } = useRequestHeaders();
  const trackingContext = useContext(TrackingContext);
  const navigation = useNavigation();
  const serviceBoxContext = useServiceBoxContext();
  const { handleResolve } = useSparkassenAssignmentForm();
  const { bankCode } = useSPKCookie();

  const { parameters = {}, updateParameters } = useThematicLinkContext() || {};
  if (!link) {
    return {
      linkProps: {
        component: 'div',
        tabIndex: -1,
      },
      anchorProps: {
        component: 'div',
        tabIndex: -1,
      },
      singleProps: {
        component: 'div',
        tabIndex: -1,
      },
    };
  }

  const overrideTarget = link.target;

  const linkPropsTitle = link.title || link.text;
  const singlePropsTitle = link.title && link.title != link.text ? link.title : undefined;

  if ((isLinkInternalLink(link) && !link.reference) || (isLinkAssetLink(link) && !link.media)) {
    return {
      linkProps: {
        component: 'div',
        tabIndex: -1,
        'aria-label': linkPropsTitle,
      },
      anchorProps: {
        component: 'div',
        tabIndex: -1,
      },
      singleProps: {
        component: 'div',
        tabIndex: -1,
        title: singlePropsTitle,
      },
    };
  }

  if (link?.reference?.slugPath) {
    const pathname =
      (link.reference.slugPath.endsWith('/') ? link.reference.slugPath : `${link.reference.slugPath}.html`) +
      (link.anchor ? `#${link.anchor}` : '');

    const href = `${internalHost || ''}${pathname}`;
    const isMenuLink = isLinkMenuLink(link);
    const isAnchorLink = link.anchor && `${router.asPath.split(/[#?]/)[0]}#${link.anchor}` === pathname;

    const handleSmoothScrolling = () => {
      const reset = () => {
        document.body.style.scrollBehavior = 'smooth';
        document.documentElement.style.scrollBehavior = 'smooth';
        router.events.off('routeChangeComplete', reset);
      };
      document.body.style.scrollBehavior = 'auto';
      document.documentElement.style.scrollBehavior = 'auto';
      router.events.on('routeChangeComplete', reset);
    };

    return {
      linkProps: {
        component: 'div',
        tabIndex: 0,
        'aria-current': checkLocation(router, pathname),
        role: 'link',
        onClick: () => {
          trackingContext?.trackLink?.({
            linkHref: href,
            linkText: link.text,
            linkType: isMenuLink ? 'LinkMenuLink' : 'LinkInternalLink',
          });
          if (isAnchorLink && !isMenuLink) {
            // eslint-disable-next-line @if6/fi-no-location
            window.location.hash = link.anchor;
            return false;
          }
          if (isMenuLink) {
            const pageId = link.reference?.publication_id || parseInt(link.reference?.id) || 0;
            if (pageId) {
              navigation?.setActiveTab(pageId);
              navigation?.setOpen(true);
            } else {
              navigation?.toggle();
            }
          } else {
            handleSmoothScrolling();
            router.push(pathname).catch((e) => {
              // eslint-disable-next-line no-console
              console.error(e);
            });
          }
        },
      },
      anchorProps: {
        component: 'a',
        tabIndex: -1,
        href,
        onClick: (event) => {
          event.preventDefault();
          if (isAnchorLink && !isMenuLink) {
            // eslint-disable-next-line @if6/fi-no-location
            window.location.hash = link.anchor;
            return false;
          }
          if (isMenuLink) {
            const pageId = link.reference?.publication_id || parseInt(link.reference?.id) || 0;
            if (pageId) {
              navigation?.setActiveTab(pageId);
              navigation?.setOpen(true);
            } else {
              navigation?.toggle();
            }
          } else {
            handleSmoothScrolling();
            router.push(pathname).catch((e) => {
              // eslint-disable-next-line no-console
              console.error(e);
            });
          }
          return false;
        },
      },
      singleProps: {
        component: 'a',
        tabIndex: 0,
        href,
        'aria-current': checkLocation(router, pathname),
        title: singlePropsTitle,
        onClick: (event) => {
          trackingContext?.trackLink?.({
            linkHref: href,
            linkText: link.text,
            linkType: isMenuLink ? 'LinkMenuLink' : 'LinkInternalLink',
          });

          if (isAnchorLink && !isMenuLink) {
            // Allow smooth scrolling
            return false;
          }

          event.preventDefault();

          if (isMenuLink) {
            const pageId = link.reference?.publication_id || parseInt(link.reference?.id) || 0;
            if (pageId) {
              navigation?.setActiveTab(pageId);
              navigation?.setOpen(true);
            } else {
              navigation?.toggle();
            }
          } else {
            handleSmoothScrolling();
            router.push(pathname).catch((e) => {
              // eslint-disable-next-line no-console
              console.error(e);
            });
          }
          return false;
        },
      },
    };
  }

  if (isLinkAssetLink(link)) {
    const pathname = `/api/download/${link.media.id}/${link.media.name}?hash=${link.media.hash}`;
    const href = `${internalHost || ''}${pathname}`;

    return {
      linkProps: {
        component: 'div',
        tabIndex: 0,
        role: 'link',
        'aria-label': linkPropsTitle,
        onClick: (event) => {
          event.preventDefault();
          event.stopPropagation();
          trackingContext?.trackLink?.({
            linkHref: href,
            linkText: link.text,
            linkType: 'LinkAssetLink',
          });
          window.open(href, '_download');
        },
      },
      anchorProps: {
        component: 'a',
        tabIndex: -1,
        href,
      },
      singleProps: {
        component: 'a',
        tabIndex: 0,
        href,
        title: singlePropsTitle,
        onClick: (event) => {
          trackingContext?.trackLink?.({
            linkHref: href,
            linkText: link.text,
            linkType: 'LinkAssetLink',
          });
          event.stopPropagation();
        },
      },
    };
  }

  if (isLinkThematischerLink(link)) {
    if (!link.key) {
      return {
        linkProps: {
          component: 'div',
          tabIndex: -1,
          disabled: true,
        },
        anchorProps: {
          component: 'div',
          tabIndex: -1,
          disabled: true,
        },
        singleProps: {
          component: 'div',
          tabIndex: -1,
          disabled: true,
        },
      };
    }

    const url = new URL(process.env.REDIRECTOR_ENDPOINT_URL);
    const paramParameter = new URLSearchParams(parameters);
    url.searchParams.set('tu_key', link.key);
    url.searchParams.set('tu_bez', link.description || 'Mehr_Infos');
    url.searchParams.set('param', paramParameter.toString());

    const handleOnClick: MouseEventHandler<HTMLElement> = async (event) => {
      event.stopPropagation();
      event.preventDefault();

      // use target url because of onmousedown defined by tag commander or another script
      const url = new URL(event.target['href'] || process.env.REDIRECTOR_ENDPOINT_URL);
      url.searchParams.set('tu_key', link.key);
      url.searchParams.set('tu_bez', link.description || 'Mehr_Infos');
      url.searchParams.set('param', paramParameter.toString());

      if (bankCode) {
        url.searchParams.set('blz', bankCode);
      }

      trackingContext?.trackLink?.({
        linkHref: url.toString(),
        linkText: link.text,
        linkType: 'LinkThematischerLink',
      });

      const thematicLinkClicked = new CustomEvent(EVENT_THEMATIC_LINK, {
        detail: {
          key: link.key,
          description: link.description,
        },
      });
      document.dispatchEvent(thematicLinkClicked);
      const validPromise = handleResolve(link.key, link.description);
      const [parameters, promisedParameters] = await Promise.all([
        updateParameters?.(link.key, link.description, 1500),
        validPromise,
      ]);
      if (parameters) {
        Object.keys(parameters).forEach((key) => {
          paramParameter.set(key, parameters[key]);
        });
      }
      url.searchParams.set('param', paramParameter.toString());

      if (!promisedParameters) {
        return;
      }

      url.searchParams.set('blz', promisedParameters.bankCode);

      // eslint-disable-next-line @if6/fi-no-location
      window.location.href = url.toString();
    };

    return {
      linkProps: {
        component: 'div',
        tabIndex: 0,
        role: 'link',
        'aria-label': linkPropsTitle,
        onClick: handleOnClick,
      },
      anchorProps: {
        component: 'a',
        href: url.toString(),
        tabIndex: -1,
      },
      singleProps: {
        component: 'a',
        href: url.toString(),
        tabIndex: 0,
        title: singlePropsTitle,
        onClick: handleOnClick,
      },
    };
  }

  if (isLinkActionLink(link)) {
    const action = (() => {
      switch (link.action) {
        case 'directoryUp':
          return () => {
            const pathName = window.location.pathname.substring(0, window.location.pathname.lastIndexOf('/'));
            const url = pathName ? `${pathName}.html` : '/';

            router.push(url).catch((e) => {
              // eslint-disable-next-line no-console
              console.error(e);
            });
          };
        case 'toTop':
          return () => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            const focusableElement = document.querySelector(
              'a:not([tabindex="-1"], button:not([tabindex="-1"], input:not([tabindex="-1"], textarea:not([tabindex="-1"], select:not([tabindex="-1"], [tabindex]:not([tabindex="-1"])'
            );
            if (focusableElement instanceof HTMLElement) {
              focusableElement.focus();
            }
          };
        case 'toggleNavigation':
          return () => {
            navigation.toggle();
          };
        case 'showPrivacyCenter':
          return () => {
            (tC as TagCommander).privacyCenter.showPrivacyCenter();
          };
        case 'openServiceBox':
          return () => {
            if (typeof window !== 'undefined' && window.matchMedia('(max-width: 1080px)').matches && !navigation.open) {
              navigation.toggle();
            }
            serviceBoxContext?.onOpen();
          };
        default: {
          if (link.action) {
            // eslint-disable-next-line no-console
            console.error(`ActionLink: ${link.action} is not supported.`);
          }
        }
      }
    })();

    return {
      linkProps: {
        component: 'div',
        tabIndex: 0,
        role: 'button',
        'aria-label': linkPropsTitle,
        onClick: () => {
          trackingContext?.trackLink?.({
            linkText: link.text,
            linkType: 'LinkActionLink',
          });
          action();
        },
      },
      anchorProps: {
        component: 'div',
        tabIndex: -1,
      },
      singleProps: {
        component: 'a',
        tabIndex: 0,
        role: 'button',
        title: singlePropsTitle,
        onClick: () => {
          trackingContext?.trackLink?.({
            linkText: link.text,
            linkType: 'LinkActionLink',
          });
          action();
        },
      },
    };
  }

  if (isLinkExternalLink(link)) {
    const rel = makeRel({
      nofollow: !isWhitelistedDomain(link.url),
      noreferrer: !isWhitelistedDomain(link.url),
      ...link.rel,
      noopener: true,
    });

    const checkWhiteListedParameters = (url, parameters, whitelist) => {
      if (!url) {
        return;
      }
      const modifiedUrl = new URL(url);
      const urlParams = new URLSearchParams(parameters);

      for (const [param, value] of urlParams) {
        if (whitelist.includes(param)) {
          if (typeof modifiedUrl !== 'string') {
            modifiedUrl.searchParams.set(param, value);
          }
        }
      }
      return modifiedUrl;
    };

    const modifiedURL = link.url ? checkWhiteListedParameters(link.url, parameters, whiteList).toString() : null;

    return {
      linkProps: {
        component: 'div',
        tabIndex: 0,
        role: 'link',
        'aria-label': linkPropsTitle,
        onClick: (event) => {
          const tagName = event.target['tagName'];
          const divElement = event.currentTarget as HTMLDivElement;
          const anchorElement = divElement.querySelector('a');
          // Trigger click event on Anchor, in click context, so we do not require window.open
          if (tagName !== 'A' && anchorElement) {
            event.preventDefault();
            event.stopPropagation();
            anchorElement.click();
          }
        },
      },
      anchorProps: {
        component: 'a',
        tabIndex: -1,
        href: link.includeParameter ? modifiedURL : link.url,
        target: overrideTarget || '_blank',
        rel,
        onClick: (event) => {
          event.stopPropagation();
          trackingContext?.trackLink?.({
            linkText: link.text,
            linkHref: link.includeParameter ? modifiedURL : link.url,
            linkType: 'LinkExternalLink',
          });
        },
      },
      singleProps: {
        component: 'a',
        tabIndex: 0,
        role: 'link',
        title: singlePropsTitle,
        href: link.includeParameter ? modifiedURL : link.url,
        target: overrideTarget || isTargetSelfDomain(link.url) || isTargetSelfDomain(modifiedURL) ? '_self' : '_blank',
        rel,
        onClick: (event) => {
          event.stopPropagation();
          trackingContext?.trackLink?.({
            linkText: link.text,
            linkHref: link.includeParameter ? modifiedURL : link.url,
            linkType: 'LinkExternalLink',
          });
        },
      },
    };
  }

  return {
    linkProps: {
      component: 'div',
      tabIndex: -1,
    },
    anchorProps: {
      component: 'div',
      tabIndex: -1,
    },
    singleProps: {
      component: 'span',
      tabIndex: -1,
    },
  };
}
